/**
 * Armadillo Adamantium Next — DNA-Level Device Fingerprinting
 *
 * Creates a composite device identity from multiple signal sources:
 * - GPU fingerprint (WebGL renderer, shader precision)
 * - Audio context fingerprint (unique audio hardware response)
 * - Canvas fingerprint (2D rendering differences)
 * - Font fingerprint (installed font enumeration)
 * - Clock skew measurement (NTP offset detection)
 * - Network timing fingerprint
 * - TLS fingerprint (if available via headers)
 *
 * This creates a device identity that survives:
 * - VPN changes
 * - Browser incognito mode
 * - Cookie clearing
 * - IP rotation
 */

import { sha256 } from 'js-sha256';
import { supabase } from '@/integrations/supabase/client';

// ============================================
// TYPES
// ============================================

export interface DeviceSignals {
  webgl_renderer: string;
  webgl_vendor: string;
  gpu_fingerprint: string;
  audio_fingerprint: string;
  canvas_fingerprint: string;
  font_fingerprint: string;
  clock_skew_ms: number;
  tls_fingerprint: string;
  http2_fingerprint: string;
}

export interface DeviceFingerprint {
  device_hash: string;
  signals: DeviceSignals;
  composite_score: number;
  is_trusted: boolean;
  is_new_device: boolean;
}

// ============================================
// GPU FINGERPRINT
// ============================================

function getGPUFingerprint(): {
  renderer: string;
  vendor: string;
  fingerprint: string;
} {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

    if (!gl) return { renderer: 'unknown', vendor: 'unknown', fingerprint: 'no-webgl' };

    const glCtx = gl as WebGLRenderingContext;

    // Get renderer and vendor
    const debugInfo = glCtx.getExtension('WEBGL_debug_renderer_info');
    const renderer = debugInfo
      ? glCtx.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) || 'unknown'
      : glCtx.getParameter(glCtx.RENDERER) || 'unknown';
    const vendor = debugInfo
      ? glCtx.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) || 'unknown'
      : glCtx.getParameter(glCtx.VENDOR) || 'unknown';

    // Shader precision fingerprint
    const shaderPrecision = glCtx.getShaderPrecisionFormat(
      glCtx.FRAGMENT_SHADER,
      glCtx.HIGH_FLOAT
    );

    // Max texture size
    const maxTextureSize = glCtx.getParameter(glCtx.MAX_TEXTURE_SIZE);

    // Max viewport dims
    const maxViewport = glCtx.getParameter(glCtx.MAX_VIEWPORT_DIMS);

    // Create a composite fingerprint
    const raw = `${renderer}|${vendor}|${shaderPrecision?.precision}|${maxTextureSize}|${maxViewport}`;

    return {
      renderer: String(renderer),
      vendor: String(vendor),
      fingerprint: sha256(raw),
    };
  } catch {
    return { renderer: 'error', vendor: 'error', fingerprint: 'gpu-error' };
  }
}

// ============================================
// AUDIO FINGERPRINT
// ============================================

async function getAudioFingerprint(): Promise<string> {
  try {
    // Use OfflineAudioContext — no autoplay restrictions, no ScriptProcessorNode,
    // no AudioWorklet complexity. Works silently in all modern browsers.
    const OfflineCtxClass = window.OfflineAudioContext || (window as any).webkitOfflineAudioContext;
    if (!OfflineCtxClass) {
      // Fallback to real AudioContext + ScriptProcessorNode for legacy
      return getLegacyAudioFingerprint();
    }

    const sampleRate = 44100;
    const duration = 0.05; // 50ms
    const offlineCtx = new OfflineCtxClass(1, Math.ceil(sampleRate * duration), sampleRate);

    const oscillator = offlineCtx.createOscillator();
    const gain = offlineCtx.createGain();

    gain.gain.value = 0.1;
    oscillator.type = 'triangle';
    oscillator.frequency.value = 10000;

    oscillator.connect(gain);
    gain.connect(offlineCtx.destination);
    oscillator.start(0);

    const renderedBuffer = await offlineCtx.startRendering();
    const channelData = renderedBuffer.getChannelData(0);

    // Compute fingerprint from rendered audio data
    let hash = 0;
    const len = Math.min(channelData.length, 4096);
    for (let i = 0; i < len; i++) {
      hash += Math.abs(channelData[i] || 0) * 1000000;
    }

    return sha256(`audio:${hash.toFixed(10)}`);
  } catch {
    return 'audio-error';
  }
}

/**
 * Legacy audio fingerprint using real AudioContext + ScriptProcessorNode.
 * Only used when OfflineAudioContext is unavailable.
 */
async function getLegacyAudioFingerprint(): Promise<string> {
  try {
    const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
    if (!AudioContextClass) return 'no-audio-context';

    const context = new AudioContextClass();

    // Handle autoplay policy
    if (context.state === 'suspended') {
      await context.resume().catch(() => {});
      if (context.state === 'suspended') {
        await context.close().catch(() => {});
        return 'audio-not-allowed';
      }
    }

    const oscillator = context.createOscillator();
    const analyser = context.createAnalyser();
    const gain = context.createGain();

    gain.gain.value = 0;
    oscillator.type = 'triangle';
    oscillator.frequency.value = 10000;

    oscillator.connect(analyser);
    analyser.connect(gain);
    gain.connect(context.destination);

    const scriptProcessor = context.createScriptProcessor(4096, 1, 1);
    analyser.connect(scriptProcessor);
    scriptProcessor.connect(gain);

    return new Promise<string>((resolve) => {
      const timeout = setTimeout(() => {
        context.close().catch(() => {});
        resolve('audio-timeout');
      }, 100);

      scriptProcessor.onaudioprocess = (event) => {
        clearTimeout(timeout);
        const output = event.outputBuffer.getChannelData(0);
        let hash = 0;
        for (let i = 0; i < Math.min(output.length, 100); i++) {
          hash += Math.abs(output[i] || 0) * 1000000;
        }
        context.close().catch(() => {});
        resolve(sha256(`audio:${hash.toFixed(10)}`));
      };

      oscillator.start(0);
    });
  } catch {
    return 'audio-error';
  }
}

// ============================================
// CANVAS FINGERPRINT
// ============================================

function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement('canvas');
    canvas.width = 200;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'no-canvas';

    // Draw text with different fonts
    ctx.textBaseline = 'top';
    ctx.font = '14px Arial';
    ctx.fillStyle = '#f60';
    ctx.fillRect(125, 1, 62, 20);
    ctx.fillStyle = '#069';
    ctx.fillText('Armadillo🦔FP', 2, 15);

    // Draw shapes
    ctx.beginPath();
    ctx.arc(50, 25, 10, 0, Math.PI * 2);
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
    ctx.fill();

    // Get the image data hash
    const dataUrl = canvas.toDataURL();
    return sha256(dataUrl);
  } catch {
    return 'canvas-error';
  }
}

// ============================================
// FONT FINGERPRINT
// ============================================

function getFontFingerprint(): string {
  try {
    const baseFonts = ['monospace', 'sans-serif', 'serif'];
    const testFonts = [
      'Arial', 'Courier New', 'Georgia', 'Times New Roman', 'Verdana',
      'Trebuchet MS', 'Palatino', 'Impact', 'Comic Sans MS', 'Lucida Console',
      'Consolas', 'Tahoma', 'Helvetica', 'Arial Black', 'Calibri',
      'Cambria', 'Candara', 'Segoe UI', 'Franklin Gothic', 'Constantia',
    ];

    const testString = 'mmmmmmmmmmlli';
    const testSize = '72px';

    const canvas = document.createElement('canvas');
    canvas.width = 300;
    canvas.height = 50;
    const ctx = canvas.getContext('2d');
    if (!ctx) return 'no-canvas';

    const detected: string[] = [];

    for (const font of testFonts) {
      let fontDetected = false;

      for (const base of baseFonts) {
        ctx.font = `${testSize} ${base}`;
        const baseWidth = ctx.measureText(testString).width;

        ctx.font = `${testSize} "${font}", ${base}`;
        const testWidth = ctx.measureText(testString).width;

        if (baseWidth !== testWidth) {
          fontDetected = true;
          break;
        }
      }

      if (fontDetected) detected.push(font);
    }

    return sha256(detected.join('|'));
  } catch {
    return 'font-error';
  }
}

// ============================================
// CLOCK SKEW MEASUREMENT
// ============================================

function measureClockSkew(): Promise<number> {
  // Clock skew measurement via external API removed to avoid CSP violations.
  // Returns 0 — server-side validation handles time-based security checks.
  return Promise.resolve(0);
}

// ============================================
// DEVICE FINGERPRINT ENGINE
// ============================================

class DeviceFingerprintEngine {
  private static instance: DeviceFingerprintEngine;
  private cachedFingerprint: DeviceFingerprint | null = null;
  private isCollecting = false;

  static getInstance(): DeviceFingerprintEngine {
    if (!DeviceFingerprintEngine.instance) {
      DeviceFingerprintEngine.instance = new DeviceFingerprintEngine();
    }
    return DeviceFingerprintEngine.instance;
  }

  /**
   * Collect all device signals and compute fingerprint
   */
  async collectFingerprint(): Promise<DeviceFingerprint> {
    if (this.cachedFingerprint) return this.cachedFingerprint;
    if (this.isCollecting) {
      // Wait for existing collection
      while (this.isCollecting) {
        await new Promise((r) => setTimeout(r, 100));
      }
      return this.cachedFingerprint!;
    }

    this.isCollecting = true;

    try {
      // Collect signals in parallel
      const [gpu, audio, clockSkew] = await Promise.all([
        Promise.resolve(getGPUFingerprint()),
        getAudioFingerprint(),
        measureClockSkew(),
      ]);

      const canvas = getCanvasFingerprint();
      const fonts = getFontFingerprint();

      const signals: DeviceSignals = {
        webgl_renderer: gpu.renderer,
        webgl_vendor: gpu.vendor,
        gpu_fingerprint: gpu.fingerprint,
        audio_fingerprint: audio,
        canvas_fingerprint: canvas,
        font_fingerprint: fonts,
        clock_skew_ms: clockSkew,
        tls_fingerprint: '', // Populated by server
        http2_fingerprint: '', // Populated by server
      };

      // Compute device hash
      const deviceHash = sha256(
        signals.webgl_renderer +
          signals.audio_fingerprint +
          signals.canvas_fingerprint +
          signals.font_fingerprint +
          signals.tls_fingerprint
      );

      // Register with server
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          const result = await (supabase.rpc as any)('register_device_signature', {
            p_user_id: user.id,
            p_signals: signals,
          });

          if (result.data) {
            this.cachedFingerprint = {
              device_hash: result.data.device_hash || deviceHash,
              signals,
              composite_score: result.data.composite_score || 0.5,
              is_trusted: result.data.is_trusted || false,
              is_new_device: result.data.is_new_device || false,
            };



            return this.cachedFingerprint;
          }
        }
      } catch (error) {

      }

      // Fallback to client-only fingerprint
      this.cachedFingerprint = {
        device_hash: deviceHash,
        signals,
        composite_score: 0.5,
        is_trusted: false,
        is_new_device: true,
      };

      return this.cachedFingerprint;
    } finally {
      this.isCollecting = false;
    }
  }

  /**
   * Get device score for security context
   */
  async getDeviceScore(): Promise<number> {
    const fp = await this.collectFingerprint();
    return fp.composite_score;
  }

  /**
   * Get device hash for security context
   */
  async getDeviceHash(): Promise<string> {
    const fp = await this.collectFingerprint();
    return fp.device_hash;
  }

  /**
   * Get signals for security context
   */
  async getSecuritySignals(): Promise<{
    device_hash: string;
    device_score: number;
    device_signals: DeviceSignals;
  }> {
    const fp = await this.collectFingerprint();
    return {
      device_hash: fp.device_hash,
      device_score: fp.composite_score,
      device_signals: fp.signals,
    };
  }

  /**
   * Clear cached fingerprint (for testing)
   */
  clearCache() {
    this.cachedFingerprint = null;
  }
}

export const deviceFingerprint = DeviceFingerprintEngine.getInstance();
export type { DeviceFingerprint as DeviceFingerprintData, DeviceSignals as DeviceSignalsData };

// ============================================
// POLISH: SCORE FUSION & ANOMALY CLUSTERING
// ============================================

import { FingerprintScore } from './types';

const fingerprintHistory = new Map<string, { hash: string; score: number; timestamp: number }[]>();
const CLUSTER_ANOMALY_THRESHOLD = 0.3;

/**
 * Compute fused fingerprint score comparing current signals against baseline
 */
export function computeFusedScore(
  current: Partial<Record<string, string>>,
  baseline: Partial<Record<string, string>>,
  consentTier: 'full' | 'limited' | 'minimal' = 'full'
): FingerprintScore {
  const scores: Record<string, number> = { gpu: 0, audio: 0, canvas: 0, clock: 0, font: 0 };
  if (current.gpu && baseline.gpu) scores.gpu = current.gpu === baseline.gpu ? 1.0 : 0.0;
  if (current.audio && baseline.audio) scores.audio = current.audio === baseline.audio ? 1.0 : 0.2;
  if (current.canvas && baseline.canvas) scores.canvas = current.canvas === baseline.canvas ? 1.0 : 0.1;
  if (current.clock && baseline.clock) {
    const diff = Math.abs(parseFloat(current.clock) - parseFloat(baseline.clock));
    scores.clock = diff < 5 ? 1.0 : diff < 20 ? 0.7 : 0.2;
  }
  if (current.fonts && baseline.fonts) scores.font = current.fonts === baseline.fonts ? 1.0 : 0.3;
  
  const weights = consentTier === 'full'
    ? { gpu: 0.25, audio: 0.20, canvas: 0.20, clock: 0.15, font: 0.20 }
    : consentTier === 'limited'
    ? { gpu: 0.30, audio: 0, canvas: 0.30, clock: 0.20, font: 0.20 }
    : { gpu: 0.40, audio: 0, canvas: 0, clock: 0.30, font: 0.30 };
  
  const compositeScore = Object.entries(weights).reduce(
    (sum, [key, w]) => sum + (scores[key] || 0) * w, 0
  );
  
  return { compositeScore, gpuMatch: scores.gpu, audioMatch: scores.audio, canvasMatch: scores.canvas, clockSkewMatch: scores.clock, fontMatch: scores.font, freshnessDays: 0, consentTier };
}

export function trackFingerprintHistory(deviceHash: string, score: number): void {
  const key = deviceHash.slice(0, 12);
  const history = fingerprintHistory.get(key) || [];
  history.push({ hash: deviceHash, score, timestamp: Date.now() });
  if (history.length > 100) history.shift();
  fingerprintHistory.set(key, history);
}

export function detectFingerprintAnomaly(deviceHash: string): { anomalous: boolean; reason: string } {
  const key = deviceHash.slice(0, 12);
  const history = fingerprintHistory.get(key);
  if (!history || history.length < 3) return { anomalous: false, reason: 'Insufficient history' };
  
  const oneHourAgo = Date.now() - 3600000;
  const recent = history.filter(h => h.timestamp > oneHourAgo);
  if (recent.length > 3) return { anomalous: true, reason: `Rapid changes (${recent.length} in 1h)` };
  
  const scores = history.map(h => h.score);
  const recentAvg = scores.slice(-5).reduce((a, b) => a + b, 0) / 5;
  const olderAvg = scores.slice(0, -5).reduce((a, b) => a + b, 0) / Math.max(1, scores.length - 5);
  if (olderAvg - recentAvg > CLUSTER_ANOMALY_THRESHOLD) return { anomalous: true, reason: 'Score degradation' };
  
  return { anomalous: false, reason: 'Consistent' };
}
